<template>
  <div class="accordion">
    <div class="template-details accordion-item">
      <h2 class="accordion-header">
        <button
          class="accordion-button"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="'#collapseMeta'"
          aria-expanded="true"
          aria-controls="collapseMeta"
        >
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="info" size="xxl" space="p-12" />
            </div>
            <div>
              <div class="h4 ff-secondary mb-0">{{ $t("template.activityMeta") }}</div>
              <div class="fw-light accordion-subtitle">{{ $t("template.activityMetaSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>
      <div id="collapseMeta" class="accordion-collapse collapse show" aria-labelledby="headingMeta">
        <div class="accordion-body pb-32 pb-xl-48">
          <form id="meta-form" class="d-flex flex-column gap-3">
            <fieldset>
              <legend class="visually-hidden">{{ $t("template.activityMeta") }}</legend>
              <v-form-input
                id="meta-base-price"
                v-model="base_price"
                :label="$t('template.basePrice')"
                type="number"
                :yup-errors-variable="errors.base_price"
                form-type="outline"
              />
              <v-form-input
                id="meta-activity-mode"
                v-model="activity_mode"
                :label="$t('template.activityMode')"
                :yup-errors-variable="errors.activity_mode"
                form-type="outline"
              />
              <v-form-input
                id="meta-general-location"
                v-model="general_location"
                :label="$t('template.generalLocation')"
                :yup-errors-variable="errors.general_location"
                form-type="outline"
              />
              <div>
                <v-form
                  id="template-update-travel-type-select"
                  :label="`${$t('template.travelType')}`"
                  :yup-errors-variable="errors.difficulty"
                  form-type="outline"
                >
                  <v-select
                    id="template-update-travel-type-select"
                    v-model="difficulty"
                    :clearable="false"
                    :options="difficultyOptions"
                    :reduce="(option: string) => option"
                    class="mb-8"
                  >
                    <template v-slot:no-options>
                      <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                    </template>
                  </v-select>
                </v-form>
              </div>
              <v-form-input
                id="meta-group-size"
                v-model="group_size"
                :label="$t('template.groupSize')"
                :yup-errors-variable="errors.group_size"
                form-type="outline"
              />
              <div class="row">
                <div class="col">
                  <v-form id="meta-start-date-form" :label="$t('template.startDate')" :yup-errors-variable="errors.start_date" form-type="outline">
                    <Datepicker
                      id="meta-start-date"
                      v-model="start_date"
                      :day-class="getEndDayClass"
                      :enable-time-picker="false"
                      :format="dateFormat"
                      :highlight="highlightedDates"
                      :input-class-name="`form-control ${!!errors.start_date ? 'is-invalid' : ''}`"
                      :month-change-on-scroll="false"
                      auto-apply
                      calendar-cell-class-name="init-date-cell"
                      class="form-datepicker"
                      @update:model-value="setHighlightedDates"
                    />
                    <template #errors>
                      <span v-if="!!errors.start_date" class="invalid-feedback d-block">
                        {{ errors.start_date }}
                      </span>
                    </template>
                  </v-form>
                </div>
                <div class="col">
                  <v-form id="meta-end-date-form" :label="$t('template.endDate')" :yup-errors-variable="errors.end_date" form-type="outline">
                    <Datepicker
                      id="meta-end-date"
                      v-model="end_date"
                      :day-class="getStartDayClass"
                      :enable-time-picker="false"
                      :format="dateFormat"
                      :highlight="highlightedDates"
                      :input-class-name="`form-control ${!!errors.end_date ? 'is-invalid' : ''}`"
                      :min-date="start_date || undefined"
                      :month-change-on-scroll="false"
                      :start-date="start_date || undefined"
                      auto-apply
                      calendar-cell-class-name="end-date-cell"
                      class="form-datepicker"
                      @update:model-value="setHighlightedDates"
                    />
                    <template #errors>
                      <span v-if="!!errors.end_date" class="invalid-feedback d-block">
                        {{ errors.end_date }}
                      </span>
                    </template>
                  </v-form>
                </div>
              </div>
            </fieldset>
            <v-button
              :is-loading="loading"
              :disabled="!meta.valid || disableButton || !meta.dirty"
              class="btn-icon w-fit align-self-end mt-32 mt-lg-48"
              size="sm"
              variant="primary"
              @click.prevent="updateMeta"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import { VForm, VFormInput } from "@uniqoders/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import api from "@/api";
import { useToast, POSITION } from "vue-toastification";
import DateHelper from "@/helpers/date/DateHelper";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import useFormValidation from "@/helpers/form";
import { useAppStore } from "@/stores/app";
import { addDays, isEqual, set } from "date-fns";
import { get } from "cypress/types/lodash";

const props = defineProps<{
  agency: string;
  template: any;
}>();
const emit = defineEmits(["reloadTemplate"]);
const { t } = useI18n();
const toast = useToast();

const loading = ref(false);
const disableButton = ref(false);

const appStore = useAppStore();
const dateFormat = appStore.settings.formatDateHyphen;
const difficultyOptions = ["easy", "medium", "hard"];

const rules = yup.object({
  base_price: yup
    .number()
    .transform((value: any, originalValue: any) => (originalValue === "" ? null : value))
    .nullable()
    .label(t("template.basePrice")),
  activity_mode: yup.string().nullable().label(t("template.activityMode")),
  general_location: yup.string().nullable().label(t("template.generalLocation")),
  difficulty: yup.string().nullable().label(t("template.difficulty")),
  group_size: yup.string().nullable().label(t("template.groupSize")),
  start_date: yup.string().nullable().label(t("template.startDate")),
  end_date: yup.string().nullable().label(t("template.endDate")),
});

function getMetaInitialValues(template: any) {

  return {
    base_price: template?.meta?.basePrice ?? "",
    activity_mode: template?.meta?.activityMode ?? "",
    general_location: template?.meta?.generalLocation ?? "",
    difficulty: template?.meta?.difficulty ?? null,
    group_size: template?.meta?.groupSize ?? "",
    start_date: template?.meta?.startDate ? new Date(template.meta.startDate) : "",
    end_date: template?.meta?.endDate ? new Date(template.meta.endDate) : "",
  };
}

const initialValues = getMetaInitialValues(props.template);

const form = useFormValidation(rules, initialValues);
const { meta, errors } = form;
const { value: base_price } = useField<string | number | undefined>("base_price");
const { value: activity_mode } = useField<string>("activity_mode");
const { value: general_location } = useField<string>("general_location");
const { value: difficulty } = useField<string | null>("difficulty");
const { value: group_size } = useField<string>("group_size");
const { value: start_date } = useField<string>("start_date");
const { value: end_date } = useField<string>("end_date");

const highlightedDates = ref<Date[]>([]);
function getStartDayClass(date: Date) {
    
  if (!start_date.value) return '';
  const startDate = new Date(start_date.value);
  if (
    isEqual(
      date,
      set(startDate, {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      })
    )
  ) {
    return 'init-marked-cell';
  }
  return '';
}
function getEndDayClass(date: Date) {
  if (!end_date.value) return '';
  const endDate = new Date(end_date.value);
  if (
    isEqual(
      date,
      set(endDate, {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      })
    )
  ) {
    return 'end-marked-cell';
  }
  return '';
}
function setHighlightedDates() {
  if (!!start_date.value && !!end_date.value) {
    const startDate = new Date(start_date.value);
    const endDate = new Date(end_date.value);
    highlightedDates.value = [];
    let date = startDate;
    while (date <= endDate) {
      highlightedDates.value.push(new Date(date));
      date = addDays(date, 1);
    }
  }
}

// Watchers to update highlightedDates when dates change
watch([start_date, end_date], setHighlightedDates, { immediate: true });

// Watch for any change in the form to re-enable the button
watch(
  [base_price, activity_mode, general_location, difficulty, group_size, start_date, end_date],
  () => {
    disableButton.value = false;
  }
);

const updateMeta = async () => {
  try {
    disableButton.value = true;
    loading.value = true;

    const currentValues = {
      base_price: base_price.value,
      activity_mode: activity_mode.value,
      general_location: general_location.value,
      difficulty: difficulty.value,
      group_size: group_size.value,
      start_date: start_date.value || null,
      end_date: end_date.value || null,
    };

    // Only include fields that have changed compared to initialValues
    const payload: Record<string, any> = {};
    (Object.keys(currentValues) as Array<keyof typeof currentValues>).forEach((key) => {
      if (currentValues[key] !== initialValues[key]) {
        payload[key] = currentValues[key];
      }
    });

    await api.activityService.updateActivityMeta(props.agency, props.template.id, payload);
    toast.success(t("general.shared.savedChanges"), { position: POSITION.BOTTOM_RIGHT });
    emit("reloadTemplate");

    loading.value = false;
  } catch (e: any) {
    disableButton.value = false;
    loading.value = false;
    toast.error(e.response?.data?.message || "An error occurred.", { position: POSITION.BOTTOM_RIGHT });
  }
};
</script>

<style scoped>
.accordion {
  margin-bottom: 2rem;
}
</style>
